/*
  This is the cpatient interaction timer store. It is used to track patient interaction time. When a user is pinned this store is set with the pinned 
  users login, and the time they were pinned. When the user is unpinned, or no action with this user has happened for an amount of time greater than
  INTERACTION_EXPIRE_TIME, then the time spend interacting with the user, as well as the number of messages sent and duration of any telehealth
  calls, is saved to the database in the PatientInteractionSessionsTable . If the user wasnt pinned and the expiry time was exceeded then after saving a new session is started.

*/
import userServices from "@/services/user/userServices"

var INTERACTION_EXPIRE_TIME = 5 * 60
var rolesThatDontCreateInteractionSessions = ["MANAGEMENT"]

const state = () => ({
  pinnedUserLogin: null,
  interactionTime: 0,
  currentTelehealthSessionStartTime: null,
  telehealthDuration: 0,
  messagesSent: 0,
  timeOfLastRecordedAction: null,
  progressNotesSaved: 0
})

// getters
const getters = {
  getTimeOfLastRecordedAction: (state, getters) => {
      return state.timeOfLastRecordedAction 
  },
  getCurrentTelehealthSessionStartTime: (state, getters) => {
      return state.currentTelehealthSessionStartTime
  },
  getPinnedUserLogin: (state, getters) => {
      return state.pinnedUserLogin
  },
  getInteractionTime: (state, getters) => {
      return state.interactionTime
  },
  getTelehealthDuration: (state, getters) => {
      return state.telehealthDuration
  },
  getMessagesSent: (state, getters) => {
      return state.messagesSent
  },
  getProgressNotesSaved: (state, getters) => {
    return state.progressNotesSaved
  },
}

// actions
const actions = {
    async startTimer({dispatch, context, getters, commit, rootGetters}, pinnedUser) {
        if(rolesThatDontCreateInteractionSessions.includes(rootGetters['currentUser/getCurrentUsersRole'])) {
            return
        }
        commit('setPinnedUserLogin', pinnedUser.login)
        var currentDate = new Date()
        var currentTime = currentDate.getTime()/1000
        commit('setTimeOfLastRecordedAction', currentTime)
    },
    
    saveUserInteractionTime({context, commit, getters, rootGetters}) {
        console.log('saving user interaction')
        if(rolesThatDontCreateInteractionSessions.includes(rootGetters['currentUser/getCurrentUsersRole'])) {
            return
        }
        var currentDate = new Date()
        var currentDateFormatted = currentDate.toLocaleString('sv').replace(' ', 'T')
        var interactionSession = {
            patientLogin: getters.getPinnedUserLogin,
            coachesLogin: rootGetters['currentUser/getCurrentUsersLogin'],
            interactionDate: currentDateFormatted,
            interactionSecondsDuration: +getters.getInteractionTime.toFixed(1),
            telehealthSecondsDurations: +getters.getTelehealthDuration.toFixed(1),
            numMessagesSent: getters.getMessagesSent,
            numProgressNotesSaved: getters.getProgressNotesSaved
        }
        userServices.SavePatientInteractionSession(interactionSession)
        commit('resetState')
    },

    async recordAction({dispatch, context, getters, commit, rootGetters}, actionType) {
        if(rolesThatDontCreateInteractionSessions.includes(rootGetters['currentUser/getCurrentUsersRole'])) {
            return
        }
        console.log('TIMER::recordAction::actionType: ', actionType)
        var currentDate = new Date()
        var currentTime = currentDate.getTime()/1000
        if(getters.getTimeOfLastRecordedAction == null) {
            commit('setTimeOfLastRecordedAction', currentTime)
        }
        // check for expiry time
        var timeSinceLastAction = currentTime - getters.getTimeOfLastRecordedAction;
        if(timeSinceLastAction > INTERACTION_EXPIRE_TIME) {
            // Expiry time has been reached.
            commit('addInteractionTime', INTERACTION_EXPIRE_TIME) 
            //Save this session and start a new one.
            dispatch('saveUserInteractionTime')
        }
        else {
            // Incremenet for new amount of interaction time
            commit('addInteractionTime', currentTime - getters.getTimeOfLastRecordedAction)
            // Record action time
            commit('setTimeOfLastRecordedAction', currentTime)
            // Record other data based on action type
            // Called from DraggableTileDashboard.vue
            if (actionType == "tileClicked") {
                
            }
            // Called from UserManagement.vue and UserProfileModal.vue
            if (actionType == "userUpdated") {

            }
            // Called from TopNavbar.vue
            if (actionType == "messageSent") {
                commit('incrementMessagesSent')
            }
            // Called from TopNavbar.vue
            if (actionType == "surveySent") {

            }
            if (actionType == "documentAssigned") {

            }
            // Called from TopNavbar.vue
            if (actionType == "openedInitialReport") {

            }
            // Called from TopNavbar.vue
            if (actionType == "openedFollowUpReport") {

            }
            // Called form ProgressNotesForm.vue
            if (actionType == "progressNoteSaved") {
                commit('incrementProgressNotesSaved')
            }
            // Called from UserMenu.vue
            if (actionType == "telehealthCallStarted") {
                commit('setCurrentTelehealthStartTime', currentTime)
            }
            // Called from StandAloneProgressNote.vue
            if (actionType == "telehealthCallEnded") {
                var telehealthDuration = currentTime - getters.getCurrentTelehealthSessionStartTime
                commit('setTelehealthDuration', telehealthDuration)
            }
            // Called from UserMenu.vue
            if (actionType == "clientViewOpened") {

            }
            // Called from UserMenue.vue
            if (actionType == "unpin") {
                dispatch('saveUserInteractionTime')
            }
        }
    },
    checkForNoInteractionDueToRole({dispatch, context, rootGetters, commit}) {
        if(rolesThatDontCreateInteractionSessions.includes(rootGetters['currentUser/getCurrentUsersRole'])) {
            return true
        }
        return false
      }
}

// mutations
const mutations = {
  setTimeOfLastRecordedAction(state, time) {
      state.timeOfLastRecordedAction = time
  },
  setPinnedUserLogin(state, pinnedUserLogin) {
      state.pinnedUserLogin = pinnedUserLogin
  },
  setInteractionTime(state, interactionTime) {
      state.interactionTime = interactionTime
  },
  addInteractionTime(state, interactionTime) {
      state.interactionTime += interactionTime
  },
  setCurrentTelehealthStartTime(state, startTime) {
      state.currentTelehealthSessionStartTime = startTime
  },
  setTelehealthDuration(state, telehealthDuration) {
      state.telehealthDuration = telehealthDuration
  },
  setMessagesSent(state, messagesSent) {
      state.messagesSent = messagesSent
  },
  setPinnedUserTime(state, time) {
      state.pinnedUserTime = time
  },
  incrementMessagesSent(state) {
      state.messagesSent += 1
  },
  incrementProgressNotesSaved(state) {
      state.progressNotesSaved += 1
  },
  resetState(state) {
    state.interactionTime = 0
    state.telehealthTime = 0
    state.messagesSent = 0
    state.currentTelehealthSessionStartTime = null
    state.timeOfLastRecordedAction = null
    state.progressNotesSaved = 0
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
